import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
  type CarouselApi,
} from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { ReactNode, useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import type { ProductCardImage } from '@src/components/product-card/product-card.types'
import { Image } from '@src/quince-components/src'

export interface CardImageCarouselProps {
  /** Full per-variant image list for the selected variant (already gender-filtered by the caller). */
  images: ProductCardImage[]
  /** Resolves the alt text for an image (shared with {@link ImageTile} for parity). */
  resolveAlt: (image: ProductCardImage) => string
  /** Loading for the FIRST slide only — inherited from the grid's first-row `eagerLoadImage`. */
  imageLoading?: 'eager' | 'lazy'
  imageQuality?: number
  imageMaxWidth?: number
  /** Wraps each slide so a tap navigates to the PDP (reuses the card's `ProductCardLink`). */
  renderSlideLink: (node: ReactNode, image: ProductCardImage, index: number) => ReactNode
  /** Fired when the user settles on a NEW slide (not the initial one) — drives the gallery-viewed event. */
  onSlideView?: (index: number) => void
  ariaLabel: string
}

/** Pointer travel (px) past which a click is treated as a swipe, not a tap → no PDP navigation. */
const DRAG_THRESHOLD_PX = 10

const rootCss = css.raw({ width: 'full', height: 'full' })
const contentCss = css.raw({ height: 'full' })
// Each slide fills the fixed-aspect tile box, so the carousel introduces no layout shift vs the single-image card.
// `& a` makes the reused ProductCardLink anchor fill the slide (same trick as the card's tap overlay), so a
// tap anywhere on the image navigates to the PDP.
const itemCss = css.raw({
  position: 'relative',
  minWidth: 0,
  height: 'full',
  '& a': { position: 'absolute', inset: 0, display: 'block' },
})
const slideImageClass = css({ position: 'absolute', inset: 0, width: '100%', height: '100%' })
const indicatorCss = css.raw({
  position: 'absolute',
  bottom: 'space-8',
  left: 0,
  right: 0,
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
})

/**
 * Mobile-only swipeable image carousel rendered INSIDE the product card (CLP + Search, `show` bucket).
 *
 * Reuses the Embla-based DS {@link Carousel}. The card's clickable surface is moved onto each slide
 * (`renderSlideLink`) so a tap opens the PDP; a swipe must not, so we suppress the click that follows a
 * drag by measuring pointer travel (Embla v8 dropped `clickAllowed`). First slide loads with the
 * inherited `imageLoading` (eager only on the grid's first row); every other slide is `lazy` to hold
 * the LCP budget.
 */
export function CardImageCarousel({
  images,
  resolveAlt,
  imageLoading,
  imageQuality,
  imageMaxWidth,
  renderSlideLink,
  onSlideView,
  ariaLabel,
}: CardImageCarouselProps) {
  const [emblaApi, setEmblaApi] = useState<CarouselApi>()
  const lastIndexRef = useRef(0)
  // Distinguish a tap (navigate) from a swipe (don't): Embla v8 dropped `clickAllowed`, so we measure
  // pointer travel ourselves. A click that follows movement past the threshold is treated as a drag.
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)

  // Register the Embla listeners in an effect with explicit teardown. The DS `Carousel` re-invokes
  // `setEmblaApi` whenever its `setEmblaApi` prop identity changes, so registering inside that callback
  // (as before) stacked a fresh `select`/`reInit` pair on the SAME api every time `onSlideView`'s
  // identity changed (variant select, tag refresh) — firing `productGalleryViewed` 2×, 3×… Keying the
  // effect on the api + `onSlideView` and returning `off(...)` guarantees exactly one live listener pair.
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap()
      if (index === lastIndexRef.current) return
      lastIndexRef.current = index
      onSlideView?.(index)
    }
    // `reInit` fires when the slide list changes (e.g. a color/variant switch). That snaps back to the
    // first slide programmatically — NOT a user swipe — so only resync the baseline; never emit here.
    const onReInit = () => {
      lastIndexRef.current = emblaApi.selectedScrollSnap()
    }
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onReInit)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onReInit)
    }
  }, [emblaApi, onSlideView])

  const handlePointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    draggedRef.current = false
  }, [])

  const handlePointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    if (!start) return
    if (
      Math.abs(event.clientX - start.x) > DRAG_THRESHOLD_PX ||
      Math.abs(event.clientY - start.y) > DRAG_THRESHOLD_PX
    ) {
      draggedRef.current = true
    }
  }, [])

  // A drag ends in a synthetic click on the slide link; block that click so swiping never navigates.
  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (draggedRef.current) {
      event.preventDefault()
      event.stopPropagation()
      draggedRef.current = false
    }
  }, [])

  return (
    <Carousel
      aria-label={ariaLabel}
      opts={{ align: 'start', containScroll: 'trimSnaps' }}
      setEmblaApi={setEmblaApi}
      css={rootCss}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerMoveCapture={handlePointerMoveCapture}
      onClickCapture={handleClickCapture}
    >
      <CarouselContent css={contentCss} viewportCss={contentCss}>
        {images.map((image, index) => (
          <CarouselItem key={image.id} css={itemCss}>
            {renderSlideLink(
              <Image
                url={image.url}
                alt={resolveAlt(image)}
                title=""
                width={image.width}
                height={image.height}
                loading={index === 0 ? imageLoading : 'lazy'}
                quality={imageQuality}
                maxWidth={imageMaxWidth}
                className={slideImageClass}
              />,
              image,
              index,
            )}
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselIndicator css={indicatorCss} />
    </Carousel>
  )
}
